import React, { useState, useMemo } from 'react';
import { StockMovement, UserRole, Product } from '../../types';
import {
  Search,
  Package,
  ShoppingBag,
  Gift,
  Users,
  Clock,
  Truck,
  FileDown,
  ChevronRight,
  Undo2,
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface StockTrackViewProps {
  movements: StockMovement[];
  products?: Product[];
  userRole?: UserRole | null;
  onReturnGroup?: (movements: StockMovement[]) => void;
}

type MovementTab =
  | 'Store Sales'
  | 'Shopify'
  | 'PR'
  | 'FnF'
  | 'Pre Order'
  | 'Store Transfer'
  | 'Warehouse Transfer';

interface GroupedRef {
  refId: string;
  items: StockMovement[];
  totalQty: number;
  latestTimestamp: string;
}

const StockTrackView: React.FC<StockTrackViewProps> = ({ movements, products = [], userRole, onReturnGroup }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<MovementTab>('Store Sales');
  const [expandedRefs, setExpandedRefs] = useState<Set<string>>(new Set());

  const tabs = useMemo<{ label: MovementTab; icon: React.ReactNode; color: string }[]>(
    () => [
      { label: 'Store Sales', icon: <ShoppingBag size={14} />, color: 'indigo' },
      { label: 'Shopify', icon: <ShoppingBag size={14} />, color: 'blue' },
      { label: 'PR', icon: <Gift size={14} />, color: 'rose' },
      { label: 'FnF', icon: <Users size={14} />, color: 'purple' },
      { label: 'Pre Order', icon: <Clock size={14} />, color: 'amber' },
      { label: 'Store Transfer', icon: <Truck size={14} />, color: 'cyan' },
      { label: 'Warehouse Transfer', icon: <Undo2 size={14} />, color: 'emerald' },
    ],
    []
  );

  const TAB_COLORS: Record<string, string> = {
    indigo: 'bg-indigo-600 border-indigo-600 shadow-indigo-100',
    blue: 'bg-blue-600 border-blue-600 shadow-blue-100',
    rose: 'bg-rose-600 border-rose-600 shadow-rose-100',
    purple: 'bg-purple-600 border-purple-600 shadow-purple-100',
    amber: 'bg-amber-600 border-amber-600 shadow-amber-100',
    cyan: 'bg-cyan-600 border-cyan-600 shadow-cyan-100',
    emerald: 'bg-emerald-600 border-emerald-600 shadow-emerald-100',
    green: 'bg-green-600 border-green-600 shadow-green-100',
  };

  // Match against the exact backend movementType enum values from stockMovementSchema
  const matchesTab = (m: StockMovement, tab: MovementTab): boolean => {
    // Prefer movementType (raw backend field); fall back to type
    const type = (m.movementType || m.type || '').toString();

    switch (tab) {
      case 'Store Sales':        return type === 'Sale';
      case 'Shopify':            return type === 'Shopify';
      case 'PR':                 return type === 'PR and Gift';
      case 'FnF':                return type === 'Friends and Family';
      case 'Pre Order':          return type === 'Pre-order';
      case 'Store Transfer':     return type === 'Warehouse to Store';   // WH → Store
      case 'Warehouse Transfer': return type === 'Store to Warehouse';   // Store → WH
      default:                   return false;
    }
  };

  // Build stockId → { productName, sizeLabel } lookup from the products list.
  // The movement table stores productSize = stockId (UUID), not a human-readable size.
  const stockLookup = useMemo(() => {
    const map: Record<string, { productName: string; sizeLabel: string }> = {};
    for (const product of products) {
      for (const stock of product.stocks) {
        if (stock.stockId) {
          map[stock.stockId] = { productName: product.name, sizeLabel: stock.size };
        }
      }
    }
    return map;
  }, [products]);

  // Resolve human-readable name + size for a movement row
  const resolveMovement = (m: StockMovement) => {
    // m.size holds the stockId UUID (from transformStockMovement: size: m.productSize)
    const entry = stockLookup[m.size] || stockLookup[m.stockId || ''];
    return {
      productName: entry?.productName || m.productName || m.productId,
      sizeLabel: entry?.sizeLabel || '—',
    };
  };

  const filteredMovements = useMemo(() => {
    return movements.filter((m) => {
      const q = searchTerm.toLowerCase();
      const { productName, sizeLabel } = resolveMovement(m);

      const matchesSearch =
        !q ||
        (m.id || '').toLowerCase().includes(q) ||
        productName.toLowerCase().includes(q) ||
        m.productId.toLowerCase().includes(q) ||
        sizeLabel.toLowerCase().includes(q) ||
        (m.performedBy || '').toLowerCase().includes(q);

      return matchesSearch && matchesTab(m, activeTab);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [movements, searchTerm, activeTab, stockLookup]);

  const groupedRefs = useMemo<GroupedRef[]>(() => {
    const map: Record<string, StockMovement[]> = {};

    filteredMovements.forEach((m) => {
      // Group by notes/reference if present; otherwise each movement is its own row
      const key = m.notes?.trim() || m.id || m.movementId || String(Math.random());
      if (!map[key]) map[key] = [];
      map[key].push(m);
    });

    return Object.entries(map)
      .map(([refId, items]) => ({
        refId,
        items: [...items].sort(
          (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        ),
        totalQty: items.reduce((sum, i) => sum + i.quantityChange, 0),
        latestTimestamp: items.reduce(
          (latest, i) =>
            !latest || new Date(i.timestamp) > new Date(latest) ? i.timestamp : latest,
          ''
        ),
      }))
      .sort(
        (a, b) =>
          new Date(b.latestTimestamp).getTime() - new Date(a.latestTimestamp).getTime()
      );
  }, [filteredMovements]);

  const toggleRef = (refId: string) => {
    setExpandedRefs((prev) => {
      const next = new Set(prev);
      next.has(refId) ? next.delete(refId) : next.add(refId);
      return next;
    });
  };

  const handlePDF = () => {
    const doc = new jsPDF('l', 'mm', 'a4');

    doc.setFontSize(18);
    doc.text(`Inventory Ledger — ${activeTab}`, 14, 20);
    doc.setFontSize(9);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 28);

    autoTable(doc, {
      startY: 35,
      head: [['Ref / Order ID', 'Date', 'Product', 'Size', 'SKU/ID', 'Qty', 'Store', 'WH']],
      body: filteredMovements.map((m) => [
        m.notes || '—',
        m.timestamp ? new Date(m.timestamp).toLocaleDateString() : 'N/A',
        m.productName,
        m.size,
        m.productId,
        `${m.quantityChange > 0 ? '+' : ''}${m.quantityChange}`,
        m.newStoreStock,
        m.newWhStock,
      ]),
      headStyles: { fillColor: [31, 41, 55], fontSize: 8, fontStyle: 'bold' },
      styles: { fontSize: 8, cellPadding: 3 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
    });

    doc.save(`NiaMia-Ledger-${activeTab.replace(/\s+/g, '-')}-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const handleDownloadAllPDF = () => {
    const doc = new jsPDF('l', 'mm', 'a4');
    const allTabs: MovementTab[] = [
      'Shopify',
      'PR',
      'FnF',
      'Pre Order',
      'Store Transfer',
      'Warehouse Transfer',
    ];

    allTabs.forEach((tab, i) => {
      const tabMovements = movements.filter((m) => matchesTab(m, tab));

      if (i > 0) doc.addPage();

      doc.setFontSize(18);
      doc.text(`Inventory Ledger — ${tab}`, 14, 20);
      doc.setFontSize(9);
      doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 28);

      if (tabMovements.length === 0) {
        doc.setFontSize(10);
        doc.setTextColor(150);
        doc.text(`No records found for ${tab}.`, 14, 42);
        doc.setTextColor(0);
        return;
      }

      autoTable(doc, {
        startY: 35,
        head: [['Ref / Order ID', 'Date', 'Product', 'Size', 'SKU/ID', 'Qty', 'Store', 'WH']],
        body: tabMovements.map((m) => [
          m.notes || '—',
          m.timestamp ? new Date(m.timestamp).toLocaleDateString() : 'N/A',
          m.productName,
          m.size,
          m.productId,
          `${m.quantityChange > 0 ? '+' : ''}${m.quantityChange}`,
          m.newStoreStock,
          m.newWhStock,
        ]),
        headStyles: { fillColor: [31, 41, 55], fontSize: 8, fontStyle: 'bold' },
        styles: { fontSize: 8, cellPadding: 3 },
        alternateRowStyles: { fillColor: [248, 250, 252] },
      });
    });

    doc.save(`NiaMia-Ledger-ALL-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const EMPTY_ICONS: Record<MovementTab, React.ReactNode> = {
    'Store Sales': <ShoppingBag size={48} />,
    Shopify: <ShoppingBag size={48} />,
    PR: <Gift size={48} />,
    FnF: <Users size={48} />,
    'Pre Order': <Clock size={48} />,
    'Store Transfer': <Truck size={48} />,
    'Warehouse Transfer': <Undo2 size={48} />,
  };

  return (
    <div
      className="p-8 h-full overflow-y-auto bg-gray-50 dark:bg-gray-900 transition-colors duration-200"
      id="stock-track-ledger"
    >
      <div className="max-w-7xl mx-auto">
        <header className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-6 print:hidden">
          <div>
            <h1 className="text-3xl font-black text-gray-900 dark:text-white tracking-tight uppercase">
              Ledger Log
            </h1>
            <p className="text-gray-500 dark:text-gray-400 text-sm font-medium mt-1">
              Categorized historical data for audit and review.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div className="relative group">
              <Search
                className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-indigo-500 transition-colors"
                size={18}
              />
              <input
                type="text"
                placeholder="Search records..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-11 pr-4 py-3 bg-white dark:bg-gray-800 border-2 border-transparent dark:border-gray-700 rounded-[1.25rem] text-sm font-bold shadow-sm focus:border-indigo-500 outline-none w-64 transition-all"
              />
            </div>

            <button
              onClick={handlePDF}
              className="flex items-center gap-2 px-6 py-3 bg-gray-900 dark:bg-indigo-600 text-white rounded-[1.25rem] font-black text-xs uppercase tracking-widest shadow-xl hover:scale-[1.02] active:scale-95 transition-all"
            >
              <FileDown size={18} /> Download PDF
            </button>

            <button
              onClick={handleDownloadAllPDF}
              className="flex items-center gap-2 px-6 py-3 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 rounded-[1.25rem] font-black text-xs uppercase tracking-widest shadow-sm hover:scale-[1.02] active:scale-95 transition-all"
            >
              <FileDown size={18} /> All Reports
            </button>
          </div>
        </header>

        <div className="flex gap-2 overflow-x-auto pb-4 mb-6 no-scrollbar print:hidden">
          {tabs.map((tab) => (
            <button
              key={tab.label}
              onClick={() => {
                setActiveTab(tab.label);
                setExpandedRefs(new Set());
              }}
              className={`flex items-center gap-2 px-5 py-3 rounded-2xl whitespace-nowrap text-[11px] font-black uppercase tracking-widest transition-all border-2 ${
                activeTab === tab.label
                  ? `${TAB_COLORS[tab.color]} text-white shadow-lg dark:shadow-none`
                  : 'bg-white dark:bg-gray-800 border-transparent text-gray-400 hover:border-gray-200 dark:hover:border-gray-600'
              }`}
            >
              {tab.icon}
              {tab.label}
              {activeTab === tab.label && groupedRefs.length > 0 && (
                <span className="ml-1 bg-white/20 text-white text-[8px] font-black px-1.5 py-0.5 rounded-full">
                  {groupedRefs.length}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-[2.5rem] shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead className="bg-gray-50/50 dark:bg-gray-900/20 border-b border-gray-100 dark:border-gray-700 text-[10px] uppercase text-gray-400 dark:text-gray-500 font-black tracking-widest">
                <tr>
                  <th className="p-5 w-10" />
                  <th className="p-5">Ref / Order ID</th>
                  <th className="p-5">Latest Activity</th>
                  <th className="p-5 text-center">Articles</th>
                  <th className="p-5 text-center">Net Qty</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                {groupedRefs.length > 0 ? (
                  groupedRefs.map((group) => {
                    const isOpen = expandedRefs.has(group.refId);

                    return (
                      <React.Fragment key={group.refId}>
                        <tr
                          onClick={() => toggleRef(group.refId)}
                          className={`cursor-pointer transition-colors select-none ${
                            isOpen
                              ? 'bg-indigo-50/60 dark:bg-indigo-900/10'
                              : 'hover:bg-gray-50/60 dark:hover:bg-gray-700/40'
                          }`}
                        >
                          <td className="p-5 text-gray-400">
                            <div className={`transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`}>
                              <ChevronRight size={16} />
                            </div>
                          </td>

                          <td className="p-5">
                            <span
                              className={`inline-flex items-center px-3 py-1.5 rounded-xl text-[10px] font-mono font-black border transition-colors ${
                                isOpen
                                  ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800'
                                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600'
                              }`}
                            >
                              {group.refId}
                            </span>
                          </td>

                          <td className="p-5">
                            <div className="flex flex-col">
                              <span className="text-xs font-black text-gray-900 dark:text-gray-200">
                                {group.latestTimestamp
                                  ? new Date(group.latestTimestamp).toLocaleDateString()
                                  : 'N/A'}
                              </span>
                              <span className="text-[10px] font-bold text-gray-400 mt-0.5 uppercase tracking-tighter">
                                {group.latestTimestamp
                                  ? new Date(group.latestTimestamp).toLocaleTimeString([], {
                                      hour: '2-digit',
                                      minute: '2-digit',
                                    })
                                  : ''}
                              </span>
                            </div>
                          </td>

                          <td className="p-5 text-center">
                            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-100 dark:bg-gray-700 text-xs font-black text-gray-600 dark:text-gray-300">
                              {group.items.length}
                            </span>
                          </td>

                          <td className="p-5 text-center">
                            <span
                              className={`inline-block px-3 py-1 rounded-full text-sm font-black ${
                                group.totalQty > 0
                                  ? 'text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20'
                                  : 'text-red-500 bg-red-50 dark:bg-red-900/20'
                              }`}
                            >
                              {group.totalQty > 0 ? '+' : ''}
                              {group.totalQty}
                            </span>
                          </td>
                        </tr>

                        {isOpen &&
                          group.items.map((m, idx) => {
                            const { productName, sizeLabel } = resolveMovement(m);
                            return (
                            <tr
                              key={m.id || idx}
                              className={`transition-all animate-in fade-in slide-in-from-top-1 duration-150 ${
                                idx % 2 === 0
                                  ? 'bg-indigo-50/20 dark:bg-indigo-900/5'
                                  : 'bg-white dark:bg-gray-800'
                              }`}
                              style={{ animationDelay: `${idx * 30}ms` }}
                            >
                              <td className="py-3 pl-10 pr-2">
                                <div className="w-px h-full bg-indigo-100 dark:bg-indigo-800 mx-auto" />
                              </td>

                              <td className="py-3 px-4" colSpan={2}>
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-xl flex items-center justify-center text-gray-400 shrink-0">
                                    <Package size={14} />
                                  </div>
                                  <div>
                                    <p className="text-xs font-black text-gray-900 dark:text-white uppercase tracking-tight leading-tight">
                                      {productName}
                                    </p>
                                    <p className="text-[9px] font-mono font-black text-indigo-500/70 uppercase mt-0.5 tracking-tighter">
                                      Size {sizeLabel}
                                    </p>
                                  </div>
                                </div>
                              </td>

                              <td className="py-3 px-4 text-center">
                                <span
                                  className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-black ${
                                    m.quantityChange > 0
                                      ? 'text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20'
                                      : 'text-red-500 bg-red-50 dark:bg-red-900/20'
                                  }`}
                                >
                                  {m.quantityChange > 0 ? '+' : ''}
                                  {m.quantityChange}
                                </span>
                              </td>

                              <td className="py-3 px-4 text-center">
                                <div className="flex items-center justify-center gap-3">
                                  <div className="text-center">
                                    <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest mb-0.5">
                                      Store
                                    </p>
                                    <p className="font-mono text-xs font-black text-gray-700 dark:text-gray-300">
                                      {m.newStoreStock}
                                    </p>
                                  </div>
                                  <div className="w-px h-6 bg-gray-200 dark:bg-gray-700" />
                                  <div className="text-center">
                                    <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest mb-0.5">
                                      WH
                                    </p>
                                    <p className="font-mono text-xs font-black text-indigo-600 dark:text-indigo-400">
                                      {m.newWhStock}
                                    </p>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          );
                          })}

                        {isOpen && (
                          <tr className="bg-indigo-50/40 dark:bg-indigo-900/10 border-b-2 border-indigo-100 dark:border-indigo-900/30">
                            <td colSpan={5} className="py-2 px-10">
                              <div className="flex items-center gap-6 text-[9px] font-black uppercase tracking-widest text-indigo-500 dark:text-indigo-400">
                                <span>
                                  {group.items.length} article{group.items.length !== 1 ? 's' : ''}
                                </span>
                                <span>·</span>
                                <span>
                                  Net qty: {group.totalQty > 0 ? '+' : ''}
                                  {group.totalQty}
                                </span>
                                <span>·</span>
                                <span>
                                  {group.latestTimestamp
                                    ? new Date(group.latestTimestamp).toLocaleString()
                                    : '—'}
                                </span>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={5} className="p-40 text-center">
                      <div className="flex flex-col items-center animate-in fade-in zoom-in-95 duration-500">
                        <div className="w-24 h-24 bg-gray-50 dark:bg-gray-900/50 rounded-full flex items-center justify-center mb-8 border border-gray-100 dark:border-gray-800 shadow-inner text-gray-200 dark:text-gray-800">
                          {EMPTY_ICONS[activeTab]}
                        </div>
                        <h3 className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-[0.2em] mb-2">
                          No {activeTab} Records
                        </h3>
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest max-w-[220px] mx-auto leading-relaxed">
                          Currently no historical data available for this logistics channel.
                        </p>
                        {searchTerm && (
                          <button
                            onClick={() => setSearchTerm('')}
                            className="mt-6 text-[9px] font-black text-indigo-500 uppercase tracking-tighter border-b border-indigo-500/30 pb-1 hover:text-indigo-400 transition-colors"
                          >
                            Clear search filters
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        @media print {
          body { margin: 0; padding: 0; }
          #stock-track-ledger { background: white !important; }
          .print\\:hidden { display: none !important; }
          table { width: 100% !important; border: 1px solid #eee !important; }
          th, td { padding: 10px !important; border-bottom: 1px solid #eee !important; }
          tr { page-break-inside: avoid; }
        }
      `}</style>
    </div>
  );
};

export default StockTrackView;